/* Mapping out the days ahead, and learning from the ones behind.
 *
 * Every planning tool is good at today and quietly hopeless at the fortnight,
 * because today is the only question a day view can ask. That is the wrong
 * distance for the thing people actually fail at: a hobby does not go missing
 * on Tuesday, it goes missing across a month of Tuesdays, and by the time the
 * day view can tell you, the month is gone.
 *
 * So this asks the further question — *where does all of this go over the next
 * fortnight* — and answers it in three parts, in this order deliberately:
 *
 *   1. **What fits.** Run the real solver across the range, carrying the
 *      backlog forward, so a task placed on Tuesday is not offered again on
 *      Wednesday.
 *   2. **What does not.** The useful half. A fortnight that swallowed nine of
 *      your eleven tasks is not a success, and the two left over are the
 *      decision you actually have to make — while there is still time to make
 *      it.
 *   3. **Why your numbers were wrong.** Measured, not guessed: the median of
 *      what things actually took against what you said they would. This is the
 *      part that makes the next fortnight's plan better than this one, and it
 *      is offered rather than applied, because only you know whether five
 *      hours was the real length or two hours of interruptions.
 *
 * Nothing here writes until asked. A proposal is a plan generated; applying it
 * is a plan accepted.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, errorMessage } from '../api';
import Dialog from './Dialog';
import { human, friendlyDate } from './time';

interface MapOutDay {
  date: string;
  weekday: string;
  working: boolean;
  away: string | null;
  placed: { title: string; start: string; end: string; taskId: string | null }[];
  minutes: number;
  freeMin: number;
}

interface Calibration {
  measured: boolean;
  factor: number;
  note: string;
  samples?: number;
}

interface MapOutResult {
  from: string;
  to: string;
  days: MapOutDay[];
  placed: number;
  minutes: number;
  calibration: Calibration;
  unplaced: { id: string; title: string; estMin: number; urgency: string; due: string | null; reason: string }[];
  applied: boolean;
}

interface Drift {
  id: string;
  title: string;
  estMin: number;
  actualMin: number;
  done: boolean;
  ratio: number;
  suggestedMin: number;
  note: string;
}

const HORIZONS = [
  { days: 3, label: 'Three days' },
  { days: 7, label: 'A week' },
  { days: 14, label: 'A fortnight' },
  { days: 30, label: 'A month' }
];

export default function MapOut({ from, defaultDays = 7, onClose }: {
  from: string;
  defaultDays?: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [days, setDays] = useState(defaultDays);
  const [result, setResult] = useState<MapOutResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdjust, setShowAdjust] = useState(false);

  const estimates = useQuery({
    queryKey: ['estimates'],
    queryFn: async () => {
      const r = await get<{ drift: Drift[]; calibration: Calibration }>('/api/estimates');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your estimates.'));
      return r;
    }
  });

  const look = useMutation({
    mutationFn: async (horizon: number) => {
      const r = await post<{ mapOut: MapOutResult }>('/api/plan/map-out',
        { from, days: horizon, apply: false });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not work that out.'));
      return r.mapOut;
    },
    onSuccess: setResult,
    onError: (e: Error) => setError(e.message)
  });

  const apply = useMutation({
    mutationFn: async () => {
      const r = await post<{ mapOut: MapOutResult }>('/api/plan/map-out',
        { from, days, apply: true });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return r.mapOut;
    },
    onSuccess: r => {
      setResult(r);
      void qc.invalidateQueries();
      onClose();
    },
    onError: (e: Error) => setError(e.message)
  });

  const correct = useMutation({
    mutationFn: async (d: Drift) => {
      const r = await patch(`/api/tasks/${d.id}`, { estMin: d.suggestedMin });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return d;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['estimates'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      /* The plan that was just proposed used the old number, so it is stale
         the moment an estimate changes. Say so rather than showing it. */
      setResult(null);
    },
    onError: (e: Error) => setError(e.message)
  });

  const drift = estimates.data?.drift ?? [];
  const calibration = result?.calibration ?? estimates.data?.calibration;
  const busiest = result?.days.filter(d => d.placed.length) ?? [];

  return (
    <Dialog
      icon="map"
      title="Map out the days ahead"
      description="Nothing is written until you say so. The useful half is what does not fit —
                   while there is still time to do something about it."
      onClose={onClose}>
      <div className="mapout">
        <fieldset className="alarm-picker">
          <legend>How far ahead</legend>
          {HORIZONS.map(h => (
            <label key={h.days} className={`chip chip-pick${days === h.days ? ' is-on' : ''}`}>
              <input type="radio" name="horizon" checked={days === h.days}
                     onChange={() => { setDays(h.days); setResult(null); }} />
              {h.label}
            </label>
          ))}
        </fieldset>

        {calibration && (
          <p className={`muted small${calibration.measured ? '' : ' is-unmeasured'}`}>
            {calibration.note}
          </p>
        )}

        <div className="inline wrap">
          <button className="btn btn-primary btn-sm"
                  onClick={() => { setError(null); look.mutate(days); }}
                  disabled={look.isPending}>
            {look.isPending ? 'Working it out…' : result ? 'Work it out again' : 'Show me what fits'}
          </button>
          {result && (
            <button className="btn btn-primary btn-sm" onClick={() => apply.mutate()}
                    disabled={apply.isPending || result.placed === 0}>
              {apply.isPending ? 'Placing…' : `Put ${result.placed} on my calendar`}
            </button>
          )}
        </div>

        {error && <p className="auth-err" role="alert">{error}</p>}

        {result && (
          <>
            <p className="mapout-headline">
              {result.placed === 0
                ? 'Nothing fitted — every day in this range is already full, or there is nothing waiting to be placed.'
                : `${result.placed} ${result.placed === 1 ? 'task' : 'tasks'}, ` +
                  `${human(result.minutes)}, across ${friendlyDate(result.from)} to ${friendlyDate(result.to)}.`}
            </p>

            {busiest.length > 0 && (
              <ul className="mapout-days">
                {busiest.map(d => (
                  <li key={d.date}>
                    <strong>{d.weekday}</strong>
                    <span className="muted small">
                      {d.placed.map(b => `${b.start} ${b.title}`).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {result.unplaced.length > 0 && (
              <div className="notice notice-bad">
                <p><strong>{result.unplaced.length} did not fit.</strong></p>
                <ul className="mapout-unplaced">
                  {result.unplaced.slice(0, 6).map(u => (
                    <li key={u.id}>
                      <strong>{u.title}</strong>
                      <span className="muted small"> · {human(u.estMin)} · {u.reason}</span>
                    </li>
                  ))}
                </ul>
                <p className="muted small">
                  This is the decision, and it is yours: drop one, move a deadline, or give
                  the work more of the week. Amon will not quietly squeeze them in.
                </p>
              </div>
            )}
          </>
        )}

        {/* ── the adjustment tool ────────────────────────────────
            "You said two hours and it took five." The app has both numbers and
            did nothing with the difference except quietly widen a budget. */}
        <div className="panel-head">
          <h3>When your estimates were wrong</h3>
          <button className="linkish" onClick={() => setShowAdjust(s => !s)}
                  aria-expanded={showAdjust}>
            {showAdjust ? 'Hide' : `Show ${drift.length || ''}`.trim()}
          </button>
        </div>

        {!showAdjust && (
          <p className="muted small">
            {drift.length
              ? `${drift.length} ${drift.length === 1 ? 'task ran' : 'tasks ran'} well over or ` +
                'under what you allowed. Correcting them makes the next fortnight honest.'
              : 'Nothing has run far enough over or under to be worth correcting yet.'}
          </p>
        )}

        {showAdjust && (
          <ul className="mapout-drift">
            {drift.length === 0 && (
              <li className="muted small">
                Nothing to correct. Estimates only become measurable once a timer has run
                against a task, or you have said how long something took.
              </li>
            )}
            {drift.map(d => (
              <li key={d.id}>
                <div>
                  <strong>{d.title}</strong>
                  <span className="muted small"> · {d.note}</span>
                </div>
                <button className="btn btn-ghost btn-sm"
                        disabled={correct.isPending}
                        onClick={() => correct.mutate(d)}>
                  Use {human(d.suggestedMin)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
